/**
 * Add a menu for changing the type of a text block (heading, paragraph, code block, ...).
 */

import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";
import type { MenuFunction } from "@/wc/text-editor/lib/core/config";
import type { EditorView } from "prosemirror-view";
import type {
    NodeSelection,
    Selection,
    TextSelection,
} from "prosemirror-state";
import type { NodeType, Schema } from "prosemirror-model";

import { type MenuItem } from "@/wc/text-editor/lib/core/menu";
import { setBlockType } from "prosemirror-commands";

interface BlockType {
    label: string;
    icon?: { name: string; library: string };
    nodeType: NodeType;
    attrs?: { [name: string]: unknown };
}

const blockTypes: (schema: Schema) => Array<BlockType> = (schema: Schema) => [
    {
        label: "Normal text",
        icon: { name: "fonts", library: "component-assets" },
        nodeType: schema.nodes.paragraph,
        attrs: {},
    },
    {
        label: "Heading 1",
        icon: { name: "type-h1", library: "component-assets" },
        nodeType: schema.nodes.heading,
        attrs: { level: 1 },
    },
    {
        label: "Heading 2",
        icon: { name: "type-h2", library: "component-assets" },
        nodeType: schema.nodes.heading,
        attrs: { level: 2 },
    },
    {
        label: "Heading 3",
        icon: { name: "type-h3", library: "component-assets" },
        nodeType: schema.nodes.heading,
        attrs: { level: 3 },
    },
    {
        label: "Heading 4",
        // The type-h4... icons don't exist in bootstrap icons
        icon: { name: "4-square", library: "component-assets" },
        nodeType: schema.nodes.heading,
        attrs: { level: 4 },
    },
    {
        label: "Heading 5",
        icon: { name: "5-square", library: "component-assets" },
        nodeType: schema.nodes.heading,
        attrs: { level: 5 },
    },
    {
        label: "Heading 6",
        icon: { name: "6-square", library: "component-assets" },
        nodeType: schema.nodes.heading,
        attrs: { level: 6 },
    },
    {
        label: "Code block",
        icon: { name: "braces", library: "component-assets" },
        nodeType: schema.nodes.code_block,
    },
];

// Based on function blockTypeItem in prosemirror-menu
const selectionHasMarkup = (
    block: BlockType,
    selection: Selection | TextSelection | NodeSelection
) => {
    const { $from, to } = selection;
    return (
        to <= $from?.end() &&
        $from.parent.hasMarkup(block.nodeType, block?.attrs)
    );
};

const setTypeMenuItem = (block: BlockType): Omit<MenuItem, "group"> => {
    return {
        type: "toggle",
        run: (state, dispatch, view) => {
            setBlockType(block.nodeType, block.attrs)(state, dispatch, view);
            view.focus();
        },
        icon: block.icon,
        order: 0,
        label: block.label,
        name: block.label,
        disabled: (state) => !setBlockType(block.nodeType, block.attrs)(state),
        active: (state) => selectionHasMarkup(block, state.selection),
    };
};

const blockTypeDropdown = (blockTypes: Array<BlockType>): MenuItem => {
    return {
        showInInlineMenu: true,
        name: "textType",
        order: 0,
        group: "base",
        showLabel: true,
        type: "dropdown",
        items: blockTypes.map((item) => setTypeMenuItem(item)),
        caret: true,
        active: (state) =>
            Boolean(
                blockTypes.find((block) =>
                    selectionHasMarkup(block, state.selection)
                )
            ),
        label: (state) => {
            const type = blockTypes.find((block) =>
                selectionHasMarkup(block, state.selection)
            );
            return type?.label || "Turn into...";
        },
        icon: (state) => {
            const type = blockTypes.find((block) =>
                selectionHasMarkup(block, state.selection)
            );
            return type?.icon;
        },
    } as MenuItem;
};

export class TextTypeMenuExtension extends BaseExtension {
    menu =
        (currentMenu: MenuFunction) =>
        (view: EditorView): Array<MenuItem> => [
            ...currentMenu(view),
            blockTypeDropdown(blockTypes(view.state.schema)),
        ];
}
